import React, { useState, useEffect } from "react";
import { Box, FormControl, Select, MenuItem, Tooltip, IconButton } from "@mui/material";
import { Icon } from "@iconify/react";
import moment from "moment";

/* ----------------------- Full-screen card helpers ----------------------- */

/* Toggleable full-screen state: locks page scroll and exits on Escape. */
export const useFullscreen = () => {
  const [isFull, setIsFull] = useState(false);
  useEffect(() => {
    if (!isFull) return undefined;
    const onKey = (e) => e.key === "Escape" && setIsFull(false);
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isFull]);
  return [isFull, setIsFull];
};

/* sx applied to a card's root Box while it is maximised. Kept below MUI's
   modal/menu z-index (1300) so dropdowns inside the card still float on top. */
export const fullscreenSx = (isFull) =>
  isFull
    ? {
        position: "fixed",
        inset: { xs: 8, md: 16 },
        zIndex: 1200,
        margin: 0,
        maxHeight: "none",
        height: "auto",
        overflow: "auto",
        boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
        "&:hover": { transform: "none" },
      }
    : {};

/* Dimmed backdrop shown behind a maximised card; click to exit. */
export const FullscreenBackdrop = ({ open, onClose }) =>
  open ? (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1199,
        backgroundColor: "rgba(16,24,40,0.55)",
        backdropFilter: "blur(1px)",
      }}
    />
  ) : null;

/* The maximise / restore icon button placed in a card header. */
export const FullscreenButton = ({ isFull, onToggle }) => (
  <Tooltip title={isFull ? "Exit full screen" : "Full screen"} arrow>
    <IconButton size="small" onClick={onToggle} sx={{ color: "var(--app-text-muted)" }}>
      <Icon icon={isFull ? "mdi:fullscreen-exit" : "mdi:fullscreen"} width="20" />
    </IconButton>
  </Tooltip>
);

export const DATE_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "Last 7 Days", value: "last_7_days" },
  { label: "Last 30 Days", value: "last_30_days" },
  { label: "Last 3 Months", value: "last_3_months" },
  { label: "Last 6 Months", value: "last_6_months" },
  { label: "Last 12 Months", value: "last_12_months" },
];

export const pct = (num, den) =>
  den > 0 ? `${((num / den) * 100).toFixed(1)}%` : "—";

/* Build the args object for the analytics API hooks from a date + the shared
   (state / district / centre) filters. Kept in one place so the cards AND the
   charts produce the EXACT same SWR key for the same inputs — SWR then dedupes
   the request and they share a single fetch instead of hitting the API twice. */
export const buildArgs = (date, filters = {}, overrides = {}) => ({
  center_selected: filters.center_ids ? "no" : "all",
  center_ids: filters.center_ids || null,
  spandan_doctor: false,
  granularity: "day",
  cases_granularity: "day",
  filter: date,
  state: filters.state || "",
  district: filters.district || "",
  // Analytics daily series counts tests TAKEN with a Pending bucket, so totals
  // match tests taken (the dashboard timeseries doesn't send this).
  with_pending: true,
  ...overrides,
});

/* Time-granularity options that make sense for each preset date range. The
   backend accepts hour | day | week | month; the first entry is the default. */
export const GRANULARITY_OPTIONS = {
  today: [{ label: "Hourly", value: "hour" }],
  last_7_days: [
    { label: "Daily", value: "day" },
    { label: "Hourly", value: "hour" },
  ],
  last_30_days: [
    { label: "Daily", value: "day" },
    { label: "Weekly", value: "week" },
  ],
  last_3_months: [
    { label: "Weekly", value: "week" },
    { label: "Monthly", value: "month" },
  ],
  last_6_months: [
    { label: "Monthly", value: "month" },
    { label: "Weekly", value: "week" },
  ],
  last_12_months: [
    { label: "Monthly", value: "month" },
    { label: "Weekly", value: "week" },
  ],
};

/* Soft status tones for cell marking */
const TONES = {
  red: { bg: "#FEECEC", color: "#C62828" },
  amber: { bg: "#FDF3E2", color: "#B26A00" },
  green: { bg: "#E9F6E9", color: "#2E7D32" },
  lilac: { bg: "#EDEBFF", color: "#5B54E0" },
};

const isEmpty = (v) =>
  v === 0 || v === "0" || v === "0.0%" || v === "—" || v === null || v === undefined;

/* Light coloured pill — falls back to plain text when value is empty/zero */
export const Pill = ({ value, tone }) => {
  if (isEmpty(value)) return <span style={{ color: "var(--app-text-muted)" }}>{value}</span>;
  const t = TONES[tone] || TONES.red;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1,
        py: 0.25,
        borderRadius: "8px",
        fontWeight: 700,
        fontSize: 12.5,
        backgroundColor: t.bg,
        color: t.color,
      }}
    >
      {value}
    </Box>
  );
};

/* Bold emphasis for total-style values */
export const Bold = ({ value }) => (
  <Box component="span" sx={{ fontWeight: 800, color: "var(--app-text)" }}>
    {value}
  </Box>
);

const cell = (tone) => (key) => (r) => <Pill value={r[key]} tone={tone} />;
const red = cell("red");
const amber = cell("amber");
const lilac = cell("lilac");

/* Per-card date dropdown shown in the table header */
export const DateFilterSelect = ({ value, onChange }) => (
  <FormControl size="small">
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        height: 34,
        borderRadius: "8px",
        fontSize: 13,
        fontWeight: 600,
        color: "var(--app-text)",
        backgroundColor: "var(--app-surface-2)",
        "& .MuiOutlinedInput-notchedOutline": { border: "1px solid var(--app-border)" },
      }}
    >
      {DATE_OPTIONS.map((o) => (
        <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const STATUS_COLORS = {
  Active: { bg: "#E7F5E7", color: "#2E7D32" },
  "Partially Active": { bg: "#FDF1E1", color: "#B26A00" },
  Dormant: { bg: "#FEECEC", color: "#C62828" },
  "Never Used": { bg: "#EEF0F2", color: "var(--app-text-muted)" },
};

const STATUS_HELP = {
  Active: "Recorded tests in the selected period.",
  "Partially Active":
    "Tested in the period but had a long zero-test gap (≥ 7 days).",
  Dormant:
    "No tests in this period, but the centre has tested before — it has gone silent and needs follow-up.",
  "Never Used":
    "No tests in this period and none ever — registered but never operationally onboarded.",
};

export const StatusBadge = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS["Never Used"];
  return (
    <Tooltip title={STATUS_HELP[status] || ""} arrow placement="top">
      <Box
        component="span"
        sx={{
          display: "inline-block",
          px: 1,
          py: 0.25,
          borderRadius: "8px",
          fontSize: 12,
          fontWeight: 700,
          backgroundColor: c.bg,
          color: c.color,
          whiteSpace: "nowrap",
          cursor: "help",
        }}
      >
        {status}
      </Box>
    </Tooltip>
  );
};

/* ----------------------- Row builders (Tables 1 & 2) ----------------------- */

export const buildProgrammeRows = (analytics) => {
  const a = analytics || {};
  const s = a.interpretationStatusCounts || {};
  const normal = s.normal || 0;
  const abnormal = s.abnormal || 0;
  const critical = s.critical || 0;
  const beyondCritical = s.beyond_critical || 0;
  const retake = s.retake || 0;
  const interpreted =
    a.totalInterpretedCount || normal + abnormal + critical + beyondCritical + retake;
  const total = a.totalTestsTaken || 0;
  const pending = Math.max(0, total - interpreted);
  const cases = a.cases || 0;
  const avgMin = a.avgInterpretationTime || 0;
  const newP = a.patientClassification?.newPatients || 0;
  const ret = a.patientClassification?.returning || 0;
  const row = (metric, value, rate = "—") => ({ metric, value, rate });
  return [
    row("Total Tests Taken", total),
    row("Tests Interpreted", interpreted, pct(interpreted, total)),
    row("Pending Interpretation", pending, pct(pending, total)),
    row("Normal Reports", normal, pct(normal, total)),
    row("Abnormal Reports", abnormal, pct(abnormal, total)),
    row("Critical Reports", critical, pct(critical, total)),
    row("Beyond Critical Reports", beyondCritical, pct(beyondCritical, total)),
    row("Retake Reports", retake, pct(retake, total)),
    row("Cases Created", cases, pct(cases, critical)),
    row("Total Unique Patients", a.totalUniquePatients ?? newP + ret),
    row("New Patients", newP),
    row("Returning Patients", ret),
    row(
      "Avg Interpretation Time",
      avgMin ? `${Math.floor(avgMin / 60)}h ${Math.floor(avgMin % 60)}m` : "—",
    ),
  ];
};

export const buildDailyRows = (timeSeries) => {
  const reports = timeSeries?.reports || [];
  const cases = timeSeries?.cases || [];
  const caseByDate = {};
  cases.forEach((c) => {
    const d = moment(c.date).format("YYYY-MM-DD");
    caseByDate[d] = (caseByDate[d] || 0) + (c.count || 0);
  });
  return reports.map((entry) => {
    const dateStr =
      entry.day || entry.date || entry.hour || entry.week || entry.month;
    const m = moment(dateStr);
    const counts = entry.statusCounts || entry.counts || {};
    const normal = Number(counts.normal || 0);
    const abnormal = Number(counts.abnormal || 0);
    const critical = Number(counts.critical || 0);
    const beyondCritical = Number(counts.beyond_critical || 0);
    const retake = Number(counts.retake || 0);
    const pending = Number(counts.pending || 0);
    const total = normal + abnormal + critical + beyondCritical + retake + pending;
    return {
      id: m.format("YYYY-MM-DD"),
      date: m.format("DD MMM YYYY"),
      day: m.format("ddd"),
      total,
      normal,
      abnormal,
      critical,
      beyondCritical,
      retake,
      pending,
      normalPct: pct(normal, total),
      criticalPct: pct(critical, total),
      retakePct: pct(retake, total),
      cases: caseByDate[m.format("YYYY-MM-DD")] || 0,
    };
  });
};

export const buildDailyTotals = (rows) => {
  const sum = (k) => rows.reduce((acc, r) => acc + (r[k] || 0), 0);
  const total = sum("total");
  const normal = sum("normal");
  const critical = sum("critical");
  const retake = sum("retake");
  return {
    date: "Total",
    total,
    normal,
    abnormal: sum("abnormal"),
    critical,
    beyondCritical: sum("beyondCritical"),
    retake,
    pending: sum("pending"),
    normalPct: pct(normal, total),
    criticalPct: pct(critical, total),
    retakePct: pct(retake, total),
    cases: sum("cases"),
  };
};

/* ----------------------------- Column defs ----------------------------- */

const metricTone = (m = "") => {
  if (/Critical/i.test(m)) return "red";
  if (/Abnormal/i.test(m)) return "amber";
  if (/Retake/i.test(m)) return "lilac";
  if (/Normal/i.test(m)) return "green";
  return null;
};

export const programmeColumns = [
  {
    key: "metric",
    label: "Metric",
    render: (r) =>
      /^Total|Cases Created$/i.test(r.metric) ? <Bold value={r.metric} /> : r.metric,
  },
  {
    key: "value",
    label: "Value",
    align: "right",
    render: (r) => {
      const tone = metricTone(r.metric);
      if (tone) return <Pill value={r.value} tone={tone} />;
      if (/^Total/i.test(r.metric)) return <Bold value={r.value} />;
      return r.value;
    },
  },
  {
    key: "rate",
    label: "Rate / % Share",
    align: "right",
    render: (r) => {
      const tone = metricTone(r.metric);
      return tone && r.rate !== "—" ? <Pill value={r.rate} tone={tone} /> : r.rate;
    },
  },
];

export const dailyColumns = [
  { key: "date", label: "Date" },
  { key: "day", label: "Day" },
  { key: "total", label: "Total Tests", align: "right", render: (r) => <Bold value={r.total} /> },
  { key: "normal", label: "Normal", align: "right" },
  { key: "abnormal", label: "Abnormal", align: "right", render: amber("abnormal") },
  { key: "critical", label: "Critical", align: "right", render: red("critical") },
  { key: "beyondCritical", label: "Beyond Critical", align: "right", render: red("beyondCritical") },
  { key: "retake", label: "Retake", align: "right", render: lilac("retake") },
  { key: "pending", label: "Pending", align: "right", render: amber("pending") },
  { key: "normalPct", label: "Normal %", align: "right" },
  { key: "criticalPct", label: "Critical %", align: "right", render: red("criticalPct") },
  { key: "retakePct", label: "Retake %", align: "right", render: lilac("retakePct") },
  { key: "cases", label: "Cases", align: "right" },
];

export const centreColumns = [
  { key: "centre", label: "Centre" },
  { key: "type", label: "Type" },
  { key: "hubCentre", label: "Hub Centre" },
  { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  { key: "lastTestDate", label: "Last Test" },
  { key: "daysSinceLastTest", label: "Days Since", align: "right" },
  { key: "totalTests", label: "Total Tests", align: "right", render: (r) => <Bold value={r.totalTests} /> },
  { key: "normal", label: "Normal", align: "right" },
  { key: "abnormal", label: "Abnormal", align: "right", render: amber("abnormal") },
  { key: "critical", label: "Critical", align: "right", render: red("critical") },
  { key: "retake", label: "Retake", align: "right", render: lilac("retake") },
  { key: "pending", label: "Pending", align: "right", render: amber("pending") },
  { key: "criticalPct", label: "Critical %", align: "right", render: red("criticalPct") },
  { key: "retakePct", label: "Retake %", align: "right", render: lilac("retakePct") },
  { key: "casesCreated", label: "Cases", align: "right" },
  {
    key: "totalPatients",
    label: "Patients",
    align: "right",
    help: "Unique patients seen at this centre in the period (distinct people, not tests).",
  },
  { key: "activeDays", label: "Active Days", align: "right" },
  { key: "activityRate", label: "Activity %", align: "right" },
  { key: "testsPerActiveDay", label: "Tests/Day", align: "right" },
];

export const hubColumns = [
  { key: "hub", label: "Hub Centre", render: (r) => <Bold value={r.hub} /> },
  { key: "totalCentres", label: "Centres", align: "right" },
  { key: "active", label: "Active", align: "right" },
  { key: "dormant", label: "Dormant", align: "right", render: red("dormant") },
  { key: "neverUsed", label: "Never Used", align: "right" },
  { key: "operationalCoverage", label: "Coverage %", align: "right" },
  { key: "totalTests", label: "Total Tests", align: "right", render: (r) => <Bold value={r.totalTests} /> },
  { key: "normal", label: "Normal", align: "right" },
  { key: "abnormal", label: "Abnormal", align: "right", render: amber("abnormal") },
  { key: "critical", label: "Critical", align: "right", render: red("critical") },
  { key: "retake", label: "Retake", align: "right", render: lilac("retake") },
  { key: "criticalPct", label: "Critical %", align: "right", render: red("criticalPct") },
  { key: "retakePct", label: "Retake %", align: "right", render: lilac("retakePct") },
  { key: "casesCreated", label: "Cases", align: "right" },
  {
    key: "patients",
    label: "Patients",
    align: "right",
    help: "Unique patients across all centres under this hub (distinct people, not tests).",
  },
];

export const districtColumns = [
  { key: "district", label: "District", render: (r) => <Bold value={r.district} /> },
  { key: "state", label: "State" },
  { key: "totalCentres", label: "Centres", align: "right" },
  { key: "active", label: "Active", align: "right" },
  { key: "totalTests", label: "Total Tests", align: "right", render: (r) => <Bold value={r.totalTests} /> },
  { key: "normal", label: "Normal", align: "right" },
  { key: "abnormal", label: "Abnormal", align: "right", render: amber("abnormal") },
  { key: "critical", label: "Critical", align: "right", render: red("critical") },
  { key: "retake", label: "Retake", align: "right", render: lilac("retake") },
  { key: "criticalPct", label: "Critical %", align: "right", render: red("criticalPct") },
  { key: "retakePct", label: "Retake %", align: "right", render: lilac("retakePct") },
  { key: "casesCreated", label: "Cases", align: "right" },
  {
    key: "patients",
    label: "Patients",
    align: "right",
    help: "Patients across all centres in this district (may double-count people seen at multiple centres).",
  },
];

export const criticalColumns = [
  { key: "patientId", label: "Patient ID" },
  { key: "patientName", label: "Patient Name" },
  { key: "age", label: "Age", align: "right" },
  { key: "gender", label: "Gender" },
  { key: "spokeCentre", label: "Spoke Centre" },
  { key: "hubCentre", label: "Hub Centre" },
  { key: "testDate", label: "Test Date" },
  { key: "findings", label: "ECG Findings" },
  { key: "bpm", label: "BPM", align: "right" },
  { key: "pr", label: "PR", align: "right" },
  { key: "qtc", label: "QTc", align: "right" },
  { key: "qrs", label: "QRS", align: "right" },
  { key: "interpreter", label: "Interpreter" },
  { key: "interpMin", label: "Interp (min)", align: "right" },
  { key: "suggestedAction", label: "Suggested Action" },
];

export const retakeColumns = [
  { key: "spokeCentre", label: "Spoke Centre" },
  { key: "hubCentre", label: "Hub Centre" },
  { key: "totalTests", label: "Total Tests", align: "right", render: (r) => <Bold value={r.totalTests} /> },
  { key: "retakeCount", label: "Retakes", align: "right", render: lilac("retakeCount") },
  { key: "retakePct", label: "Retake %", align: "right", render: red("retakePct") },
  { key: "leadReversal", label: "Lead Reversal", align: "right" },
  { key: "baselineWander", label: "Baseline", align: "right" },
  { key: "acInterference", label: "AC Interf.", align: "right" },
  { key: "leadNotClear", label: "Lead Unclear", align: "right" },
  { key: "topReason", label: "Top Reason" },
];

export const demoColumns = [
  { key: "ageGroup", label: "Age Group" },
  { key: "gender", label: "Gender" },
  {
    key: "totalPatients",
    label: "Patients",
    align: "right",
    help: "Number of unique patients in this age/gender group (distinct people, not tests). One patient can have many tests.",
  },
  { key: "totalTests", label: "Tests", align: "right", render: (r) => <Bold value={r.totalTests} /> },
  {
    key: "testsPerPatient",
    label: "Tests/Patient",
    align: "right",
    help: "Average tests per patient = Total Tests ÷ Patients. Higher means patients are getting repeat ECGs.",
  },
  { key: "normal", label: "Normal", align: "right" },
  { key: "abnormal", label: "Abnormal", align: "right", render: amber("abnormal") },
  { key: "critical", label: "Critical", align: "right", render: red("critical") },
  { key: "retake", label: "Retake", align: "right", render: lilac("retake") },
  { key: "criticalPct", label: "Critical %", align: "right", render: red("criticalPct") },
  { key: "avgBpm", label: "Avg BPM", align: "right" },
];

export const caseColumns = [
  { key: "caseId", label: "Case ID" },
  { key: "caseDate", label: "Case Date" },
  { key: "patientId", label: "Patient ID" },
  { key: "patientName", label: "Patient Name" },
  { key: "spokeCentre", label: "Spoke Centre" },
  { key: "hubCentre", label: "Hub Centre" },
  { key: "status", label: "Status" },
  { key: "daysOpen", label: "Days Open", align: "right" },
];
